import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, useToast } from '@vd/ui';
import { api } from '../../lib/api';
import type { Project } from '@vd/types';
import { ProjectAccessPicker } from './ProjectAccessPicker';
import styles from './ProjectAccessPicker.module.css';

export interface ProjectAccessModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  /** Member being edited. */
  user: { id: string; name: string; email: string };
  /** Current state for prefill. */
  initialAccessMode: 'all_projects' | 'restricted';
  initialProjectIds: string[];
  /** All projects in the company, fetched by the parent so we don't double-query. */
  projects: Project[];
}

/**
 * Modal for setting a single member's project-access scope after they're
 * already in the company. For invite-time access (which we want most of
 * the time so a restricted invitee never has a window of full access),
 * the Members tab invite modal uses the same ProjectAccessPicker
 * directly.
 *
 * Save is disabled when "Specific" is chosen with zero projects
 * selected, mirroring the server validation. The server is the source
 * of truth on cross-company project IDs and owner-immunity; this
 * modal trusts the parent to gate visibility (owners never see the
 * menu item that opens this modal).
 */
export function ProjectAccessModal({
  open,
  onClose,
  companyId,
  user,
  initialAccessMode,
  initialProjectIds,
  projects,
}: ProjectAccessModalProps) {
  const qc = useQueryClient();
  const toast = useToast();

  const [accessMode, setAccessMode] = useState<'all_projects' | 'restricted'>(initialAccessMode);
  const [selected, setSelected] = useState<Set<string>>(new Set(initialProjectIds));
  const [search, setSearch] = useState('');

  // Reset every reopen — without this, editing a second member after
  // a first would carry over the first member's selections.
  useEffect(() => {
    if (open) {
      setAccessMode(initialAccessMode);
      setSelected(new Set(initialProjectIds));
      setSearch('');
    }
  }, [open, initialAccessMode, initialProjectIds]);

  const mutation = useMutation({
    mutationFn: (body: { accessMode: 'all_projects' | 'restricted'; projectIds?: string[] }) =>
      api.updateMemberAccess(companyId, user.id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members', companyId] });
      toast.push({
        variant: 'success',
        title: 'Access updated',
        description: accessMode === 'all_projects'
          ? `${user.name} can see all projects.`
          : `${user.name} can see ${selected.size} project${selected.size === 1 ? '' : 's'}.`,
      });
      onClose();
    },
    onError: (e) => {
      toast.push({ variant: 'danger', title: 'Update failed', description: (e as Error).message });
    },
  });

  const canSave = !mutation.isPending && (
    accessMode === 'all_projects' || selected.size > 0
  );

  const save = () => {
    if (!canSave) return;
    mutation.mutate(
      accessMode === 'all_projects'
        ? { accessMode: 'all_projects' }
        : { accessMode: 'restricted', projectIds: Array.from(selected) },
    );
  };

  return (
    <Modal open={open} onClose={onClose} width={720}>
      <ModalHeader>Project access for {user.name}</ModalHeader>
      <ModalBody>
        <p className={styles.subtle}>
          Choose which projects {user.name} can see in this workspace. Owners always see every project.
        </p>
        <ProjectAccessPicker
          accessMode={accessMode}
          setAccessMode={setAccessMode}
          selected={selected}
          setSelected={setSelected}
          search={search}
          setSearch={setSearch}
          projects={projects}
          subjectLabel={user.name}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!canSave}>
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
